import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ChevronRight, ChevronDown, RefreshCw, Search, ArrowDown, ArrowUp, Calendar, Check, FileDown } from 'lucide-react';

/**
 * Europcar · Kaynak Raporu — hangi kaynaktan kaç rezervasyon, hangi kaynağa ne harcandı.
 *
 * Tek sayfa, tarih aralığı başlığın sağ üstünde; aralık her bölümü sürer:
 *   1. Özet kartları        GET /api/europcar-kaynak/detail     (yalnız seçili aralık, kıyas yok)
 *   2. Kanal tablosu        GET /api/europcar-kaynak/table      (dönem × kaynak, bir önceki döneme göre renk)
 *   3. Google / Meta kampanyaları  (detail ucundan; GA4 oturum kampanyası ↔ platform kampanya adı)
 *   4. Lokasyon × ay        GET /api/europcar-kaynak/locations  (GA4 purchase pickup_city; yalnız Oca–Ağu 2026 dolu)
 *
 * Sadece Europcar (App.js allowBrandIds + Sidebar restrictedBrands). Bilerek yalnız açık tema.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', up: '#047857', down: '#B91C1C'
};
// Doğrulanmış kategorik sıra (dataviz validator, açık zemin): renk kaynağa bağlı, sıraya değil.
const RENK = {
  google_ads: '#436AEB', meta_ads: '#eb6834', google_organik: '#1baf7a', direkt: '#eda100', diger: '#C4C9D4'
};
const renkOf = (key) => RENK[key] || RENK.diger;
const META_SOFT = '#FDF1EB';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const nf = (n) => Math.round(Number(n || 0)).toLocaleString('tr-TR');
const tl = (n) => `₺${nf(n)}`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `₺${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (Math.abs(v) >= 1e4) return `₺${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B`;
  return tl(v);
};
const pct = (x, d = 0) => `%${(Number(x || 0) * 100).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d })}`;
const payPct = (x) => (x > 0 && x < 0.005 ? '<%1' : pct(x));
const roasYazi = (v) => (v == null || !Number.isFinite(v) ? '—' : `${v.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}×`);
const AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const AY_TAM = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const gunEtiket = (iso) => `${Number(iso.slice(8, 10))} ${AY[Number(iso.slice(5, 7)) - 1]}`;
const aralikEtiket = (a, b) => `${gunEtiket(a)}${a.slice(0, 4) !== b.slice(0, 4) ? ` ${a.slice(0, 4)}` : ''} – ${gunEtiket(b)} ${b.slice(0, 4)}`;
const ayKisa = (ym) => `${AY[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;
const dayCount = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000) + 1;

const iso = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

/* ---------------------------------------------------------------- tarih aralığı */

const ARALIKLAR = [
  { key: 'buay', label: 'Bu ay' },
  { key: 'gecenay', label: 'Geçen ay' },
  { key: '30', label: 'Son 30 gün' },
  { key: '3ay', label: 'Son 3 ay' },
  { key: '6ay', label: 'Son 6 ay' },
  { key: 'buyil', label: 'Bu yıl' },
  { key: '12ay', label: 'Son 12 ay' },
  { key: 'tumu', label: 'Ocak 2025’ten' },
  { key: 'ozel', label: 'Özel aralık' }
];
function aralikOf(key) {
  const dun = new Date(); dun.setDate(dun.getDate() - 1);
  const ayBasi = (geri) => iso(new Date(dun.getFullYear(), dun.getMonth() - geri, 1));
  if (key === 'buay') return [ayBasi(0), iso(dun)];
  if (key === 'gecenay') {
    const b = new Date(); b.setDate(1); b.setMonth(b.getMonth() - 1);
    const s = new Date(); s.setDate(0);
    return [iso(b), iso(s)];
  }
  if (key === '30') { const d = new Date(dun); d.setDate(d.getDate() - 29); return [iso(d), iso(dun)]; }
  if (key === '3ay') return [ayBasi(2), iso(dun)];
  if (key === '6ay') return [ayBasi(5), iso(dun)];
  if (key === 'buyil') return [`${dun.getFullYear()}-01-01`, iso(dun)];
  if (key === '12ay') return [ayBasi(11), iso(dun)];
  if (key === 'tumu') return ['2025-01-01', iso(dun)];
  return null;
}
const varsayilanGrain = (r) => {
  const gun = dayCount(r[0], r[1]);
  return gun <= 31 ? 'day' : gun <= 100 ? 'week' : 'month';
};

/* ---------------------------------------------------------------- parçacıklar */

function Card({ children, style, className = '', id }) {
  return (
    <div id={id} className={className}
      style={{ background: C.card, borderRadius: 18, border: `1px solid ${C.hair}`, boxShadow: '0 1px 2px rgba(17,24,39,.04), 0 8px 24px rgba(17,24,39,.04)', ...style }}>
      {children}
    </div>
  );
}

function BolumBaslik({ id, eyebrow, title, sub, right }) {
  return (
    <div id={id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginTop: 56, marginBottom: 16, scrollMarginTop: 24 }}>
      <div style={{ minWidth: 0 }}>
        {eyebrow ? <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>{eyebrow}</div> : null}
        <h2 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.022em', margin: '2px 0 0', lineHeight: 1.15, color: C.ink }}>{title}</h2>
        {sub ? <div style={{ fontSize: 15, color: C.muted, marginTop: 6, maxWidth: 760, lineHeight: 1.45 }}>{sub}</div> : null}
      </div>
      {right}
    </div>
  );
}

function SegBtn({ items, value, onChange }) {
  return (
    <div className="pdf-gizle" style={{ display: 'inline-flex', background: '#ECEEF4', borderRadius: 10, padding: 3, gap: 2, flexWrap: 'wrap' }}>
      {items.map((p) => {
        const on = value === p.key;
        return (
          <button key={p.key} type="button" onClick={() => onChange(p.key)}
            style={{ border: 0, cursor: 'pointer', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: on ? 600 : 500,
              background: on ? '#FFFFFF' : 'transparent', color: on ? C.ink : C.ink2, boxShadow: on ? '0 1px 3px rgba(17,24,39,.12)' : 'none' }}>
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

function Kpi({ label, value, lines, renk }) {
  return (
    <Card style={{ padding: '22px 22px 20px', position: 'relative', overflow: 'hidden' }}>
      {renk ? <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: renk }} /> : null}
      <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 34, fontWeight: 700, letterSpacing: '-0.025em', color: C.ink, marginTop: 8, lineHeight: 1.05 }} className="tabular-nums">
        {value}
      </div>
      <div style={{ marginTop: 10, display: 'grid', gap: 3 }}>
        {lines.filter(Boolean).map((l) => (
          <div key={l[0]} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, color: C.muted }} className="tabular-nums">
            <span>{l[0]}</span><span style={{ color: C.ink2, fontWeight: 600 }}>{l[1]}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Tek satırlık %100 yığılmış çubuk — payları yan yana kıyaslamak için. */
function PayCubugu({ title, items, toplam, fmt }) {
  const dolu = items.filter((x) => x.v > 0);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{title}</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.ink2 }} className="tabular-nums">{fmt(toplam)}</div>
      </div>
      <div style={{ display: 'flex', gap: 2, height: 16, marginTop: 10, borderRadius: 8, overflow: 'hidden', background: C.hair }}>
        {dolu.map((x) => (
          <div key={x.key} title={`${x.label}: ${fmt(x.v)} (${pct(x.v / toplam)})`}
            style={{ width: `${(x.v / toplam) * 100}%`, background: renkOf(x.key), minWidth: 2 }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginTop: 12 }}>
        {dolu.map((x) => (
          <div key={x.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.ink2 }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: renkOf(x.key) }} />
            {x.label}
            <span style={{ color: C.muted }} className="tabular-nums">{fmt(x.v)} · {payPct(x.v / toplam)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- kanal tablosu */

const donemEtiket = (x, grain) => {
  if (grain === 'month') return `${AY_TAM[Number(x.bas.slice(5, 7)) - 1]} ${x.bas.slice(0, 4)}`;
  if (grain === 'week') return `${gunEtiket(x.bas)} – ${gunEtiket(x.son)}${x.son.slice(0, 4) !== x.bas.slice(0, 4) ? ` ${x.son.slice(0, 4)}` : ''}`;
  const g = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'][new Date(`${x.bas}T00:00:00`).getDay()];
  return `${gunEtiket(x.bas)} ${x.bas.slice(0, 4)} · ${g}`;
};
const GRAIN = [{ key: 'day', label: 'Gün' }, { key: 'week', label: 'Hafta' }, { key: 'month', label: 'Ay' }];
const METRIK = [
  { key: 'rez', label: 'Rezervasyon' },
  { key: 'ciro', label: 'Ciro' },
  { key: 'aov', label: 'Ort. değer' },
  { key: 'cr', label: 'Dönüşüm oranı' }
];
const KUCUK = 10; // bu kadar rezervasyonun altında yüzde değişim renklenmez
const GALT_ETIKET = { marka: 'Marka', marka_disi: 'Marka dışı', pmax: 'PMax' };

/** Hücre değeri — metrik seçimine göre. h = { rez, ciro, oturum }. */
const deger = (h, metrik) => {
  if (!h) return null;
  if (metrik === 'rez') return h.rez;
  if (metrik === 'ciro') return h.ciro;
  if (metrik === 'aov') return h.rez ? h.ciro / h.rez : null;
  return h.oturum ? h.rez / h.oturum : null;
};
const toplamlik = (m) => m === 'rez' || m === 'ciro'; // gün sayısına göre ölçeklenen metrikler
const fmtMetrik = (v, m) => {
  if (v == null) return '—';
  if (m === 'rez') return nf(v);
  if (m === 'ciro' || m === 'aov') return tlShort(v);
  return pct(v, 2);
};

/**
 * Değişim: bir önceki döneme göre. Kısmi dönemde (devam eden ay, aralığın kestiği ilk/son dönem)
 * günlük ortalama kıyaslanır — 5 günlük Ekim'i 30 günlük Eylül'le ham kıyaslamak hep düşüş gösterir.
 */
function degisim(cur, prev, curGun, prevGun, metrik, kismi) {
  if (cur == null || prev == null || !prev) return null;
  if (kismi && toplamlik(metrik) && curGun && prevGun) return (cur / curGun) / (prev / prevGun) - 1;
  return cur / prev - 1;
}
// ters: düşüşü iyi olan metrik (rezervasyon başı maliyet) — renk tersine döner, ok yönü değişmez.
function tint(d, notr, ters) {
  if (d == null || !Number.isFinite(d) || Math.abs(d) < 0.02) return 'transparent';
  const a = Math.min(0.06 + Math.abs(d) * 0.3, 0.24);
  if (notr) return `rgba(67,106,235,${a * 0.6})`;
  return (ters ? d < 0 : d > 0) ? `rgba(16,185,129,${a})` : `rgba(239,68,68,${a})`;
}
function DegisimYazi({ d, notr, kismi, fark, ters }) {
  // Küçük taban: yüzde yanıltır (1 → 6 = %500), renksiz adet farkı.
  if (fark != null) {
    return <div style={{ fontSize: 11, fontWeight: 600, color: C.faint, marginTop: 2 }} title="Az rezervasyon — yüzde yerine adet farkı">{fark > 0 ? `+${fark}` : fark === 0 ? '±0' : fark}</div>;
  }
  if (d == null || !Number.isFinite(d)) return null;
  const renk = Math.abs(d) < 0.02 ? C.faint : notr ? C.ink2 : (ters ? d < 0 : d > 0) ? C.up : C.down;
  const v = Math.abs(d);
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: renk, marginTop: 2 }} title={kismi ? 'Günlük ortalamaya göre' : 'Bir önceki döneme göre'}>
      {d > 0 ? '▲' : d < 0 ? '▼' : ''} {v >= 10 ? '>%999' : pct(v, v < 0.1 ? 1 : 0)}{kismi ? '*' : ''}
    </div>
  );
}

function Hucre({ v, metrik, d, notr, kismi, kalin, sol, fmt, kucuk, fark, bg, ters }) {
  if (kucuk) d = null;
  const t = tint(d, notr, ters);
  return (
    <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap', background: t !== 'transparent' ? t : bg || 'transparent', borderLeft: sol ? `1px solid ${C.line}` : undefined, verticalAlign: 'top' }}>
      <div style={{ fontWeight: kalin ? 700 : 500, color: v ? C.ink : C.faint, fontSize: 14 }}>{fmt ? fmt(v) : fmtMetrik(v, metrik)}</div>
      <DegisimYazi d={d} notr={notr} kismi={kismi} fark={kucuk ? fark : undefined} ters={ters} />
    </td>
  );
}

function KanalTablosu({ data, loading, grain, setGrain }) {
  const [metrik, setMetrik] = useState('rez');
  const [googleAcik, setGoogleAcik] = useState(false);

  const donemler = data?.donemler || [];
  const g = data?.grain || grain;
  // Satırlar yeniden eskiye; her satırın "öncesi" bir alttaki, en alttakinin öncesi data.onceki.
  const oncesi = (i) => (i + 1 < donemler.length ? donemler[i + 1] : data?.onceki);
  const gunOf = (x) => (x ? x.gunSayisi || dayCount(x.bas, x.son) : 0);
  const T = data?.toplam;

  const kolonlar = [
    { key: 'google_ads', label: 'Google Ads', acilir: true },
    ...(googleAcik ? ['marka', 'marka_disi', 'pmax'].map((k) => ({ key: k, label: GALT_ETIKET[k], alt: true })) : []),
    { key: 'meta_ads', label: 'Meta Ads' },
    { key: 'google_organik', label: 'Google Organik' },
    { key: 'direkt', label: 'Direkt' },
    { key: 'diger', label: 'Diğer' }
  ];
  const hucreOf = (x, col) => (col.alt ? x.g[col.key] : x.k[col.key]);
  const roas = (ciro, h) => (h ? ciro / h : null);
  const fmtTl = (v) => (v ? tlShort(v) : '—');

  const th = { padding: '10px 14px', fontWeight: 500, fontSize: 12, color: C.muted, textAlign: 'right', whiteSpace: 'nowrap', background: '#fff' };
  const gapMi = (x) => x.bas <= '2026-01-08' && x.son >= '2025-12-09';
  const harcamaYok = (x) => x.son < (data?.meta?.spendFrom || '');

  const satir = (x, i, toplamSatiri) => {
    const o = toplamSatiri ? null : oncesi(i);
    const kismi = !toplamSatiri && x.kismi;
    const cg = gunOf(x);
    const og = gunOf(o);
    const d = (cur, prev, m = metrik, k = kismi) => (o ? degisim(cur, prev, cg, og, m, k) : null);
    const harcamaD = (cur, prev) => d(cur, prev, 'ciro');
    const ozelGun = !toplamSatiri && (gapMi(x) || (o && gapMi(o)));
    const zemin = toplamSatiri ? '#FAFBFF' : '#fff';
    const oHarcamaVar = o && !(o.son < data.meta.spendFrom);
    return (
      <tr key={toplamSatiri ? 'toplam' : x.bas} style={{ borderTop: toplamSatiri ? `2px solid ${C.line}` : `1px solid ${C.hair}`, background: zemin }}>
        <td style={{ padding: '10px 14px 10px 24px', whiteSpace: 'nowrap', fontWeight: 600, color: C.ink, fontSize: 14, verticalAlign: 'top', position: 'sticky', left: 0, background: zemin, zIndex: 1 }}>
          {toplamSatiri ? 'Seçili aralık' : donemEtiket(x, g)}
          <div style={{ fontSize: 11, fontWeight: 500, color: C.faint, marginTop: 2 }}>
            {toplamSatiri ? aralikEtiket(data.start, data.end) : kismi ? `kısmi · ${cg} gün` : gapMi(x) ? 'GA4 ölçüm eksik' : ''}
          </div>
        </td>
        {kolonlar.map((col) => {
          const h = hucreOf(x, col);
          const ph = o ? (col.alt ? (o.g ? o.g[col.key] : null) : o.k[col.key]) : null;
          const kucuk = !ozelGun && ph && (ph.rez < KUCUK || h.rez < KUCUK);
          const fark = metrik === 'rez' && !kismi && ph ? Math.round(h.rez - ph.rez) : undefined;
          return <Hucre key={col.key} v={deger(h, metrik)} metrik={metrik} d={ozelGun ? null : d(deger(h, metrik), deger(ph, metrik))} kismi={kismi && toplamlik(metrik)} kucuk={kucuk} fark={fark} />;
        })}
        <Hucre v={deger(x.toplam, metrik)} metrik={metrik} kalin d={ozelGun ? null : d(deger(x.toplam, metrik), o ? deger(o.toplam, metrik) : null)} kismi={kismi && toplamlik(metrik)} />
        {harcamaYok(x) ? (
          <td colSpan={4} style={{ padding: '10px 14px', textAlign: 'center', color: C.faint, fontSize: 12, borderLeft: `1px solid ${C.line}` }}>harcama verisi yok</td>
        ) : (
          <>
            <Hucre sol bg={toplamSatiri ? undefined : '#FBFCFF'} v={x.googleHarcama} fmt={fmtTl} notr d={oHarcamaVar ? harcamaD(x.googleHarcama, o.googleHarcama) : null} kismi={kismi} />
            <Hucre bg={toplamSatiri ? undefined : '#FBFCFF'} kalin v={roas(x.k.google_ads.ciro, x.googleHarcama)} fmt={roasYazi} kucuk={!o || o.k.google_ads.rez < KUCUK || x.k.google_ads.rez < KUCUK}
              d={ozelGun || !oHarcamaVar ? null : degisim(roas(x.k.google_ads.ciro, x.googleHarcama), roas(o.k.google_ads.ciro, o.googleHarcama), 0, 0, 'roas', false)} />
            <Hucre sol bg={toplamSatiri ? undefined : '#FFFCFA'} v={x.metaHarcama} fmt={fmtTl} notr d={oHarcamaVar ? harcamaD(x.metaHarcama, o.metaHarcama) : null} kismi={kismi} />
            <Hucre bg={toplamSatiri ? undefined : '#FFFCFA'} kalin v={roas(x.k.meta_ads.ciro, x.metaHarcama)} fmt={roasYazi} kucuk={!o || o.k.meta_ads.rez < KUCUK || x.k.meta_ads.rez < KUCUK}
              d={ozelGun || !oHarcamaVar ? null : degisim(roas(x.k.meta_ads.ciro, x.metaHarcama), roas(o.k.meta_ads.ciro, o.metaHarcama), 0, 0, 'roas', false)} />
          </>
        )}
      </tr>
    );
  };

  const kirpildi = data && !loading && data.grain === grain && (data.start !== data.istenenStart);

  return (
    <Card style={{ padding: '18px 0 6px' }}>
      <div style={{ padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <SegBtn items={METRIK} value={metrik} onChange={setMetrik} />
        <SegBtn items={GRAIN} value={grain} onChange={setGrain} />
      </div>
      {kirpildi ? (
        <div style={{ padding: '10px 24px 0', fontSize: 12.5, color: C.muted }}>
          {g === 'day' ? 'Günlük kırılımda en fazla 120 gün' : 'Haftalık kırılımda en fazla 400 gün'} gösterilir: {aralikEtiket(data.start, data.end)}.
        </div>
      ) : null}
      {!data && loading ? <div style={{ padding: 24, fontSize: 13, color: C.muted }}>Yükleniyor…</div> : null}
      {donemler.length ? (
        <div className="overflow-auto" style={{ marginTop: 14, maxHeight: 820, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 14, minWidth: googleAcik ? 1300 : 1080 }} className="tabular-nums">
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th rowSpan={2} style={{ ...th, textAlign: 'left', paddingLeft: 24, position: 'sticky', left: 0, zIndex: 4, verticalAlign: 'bottom', borderBottom: `1px solid ${C.line}` }}>Dönem</th>
                <th colSpan={kolonlar.length + 1} style={{ ...th, textAlign: 'center', color: C.ink2, fontWeight: 600, paddingBottom: 4 }}>
                  Rezervasyonun geldiği kaynak · {METRIK.find((m) => m.key === metrik).label.toLocaleLowerCase('tr-TR')}
                </th>
                <th colSpan={2} style={{ ...th, textAlign: 'center', color: C.accent, fontWeight: 700, background: C.accentSoft, borderLeft: `1px solid ${C.line}`, borderTopLeftRadius: 10 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 4, background: RENK.google_ads }} />Google Ads</span>
                </th>
                <th colSpan={2} style={{ ...th, textAlign: 'center', color: '#C2410C', fontWeight: 700, background: META_SOFT, borderLeft: `1px solid ${C.line}`, borderTopRightRadius: 10 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 4, background: RENK.meta_ads }} />Meta Ads</span>
                </th>
              </tr>
              <tr>
                {kolonlar.map((col) => (
                  <th key={col.key} style={{ ...th, cursor: col.acilir ? 'pointer' : 'default', background: col.alt ? '#FAFBFF' : '#fff', borderBottom: `1px solid ${C.line}` }}
                    onClick={col.acilir ? () => setGoogleAcik((v) => !v) : undefined}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {col.alt ? <span style={{ color: C.faint }}>↳</span> : <span style={{ width: 8, height: 8, borderRadius: 4, background: renkOf(col.key) }} />}
                      {col.label}
                      {col.acilir ? <ChevronRight size={14} style={{ color: C.faint, transform: googleAcik ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} /> : null}
                    </span>
                  </th>
                ))}
                <th style={{ ...th, color: C.ink, fontWeight: 600, borderBottom: `1px solid ${C.line}` }}>Toplam</th>
                <th style={{ ...th, background: C.accentSoft, borderLeft: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}>Harcama</th>
                <th style={{ ...th, background: C.accentSoft, borderBottom: `1px solid ${C.line}`, color: C.ink2, fontWeight: 600 }}>ROAS</th>
                <th style={{ ...th, background: META_SOFT, borderLeft: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}>Harcama</th>
                <th style={{ ...th, background: META_SOFT, borderBottom: `1px solid ${C.line}`, color: C.ink2, fontWeight: 600, paddingRight: 24 }}>ROAS</th>
              </tr>
            </thead>
            <tbody>
              {donemler.map((x, i) => satir(x, i, false))}
              {T ? satir(T, -1, true) : null}
            </tbody>
          </table>
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: C.faint, padding: '14px 24px 10px', lineHeight: 1.6 }}>
        Her hücrede bir önceki {g === 'month' ? 'aya' : g === 'week' ? 'haftaya' : 'güne'} göre değişim: yeşil artış, kırmızı düşüş, harcamada mavi.
        ROAS = o kaynaktan gelen GA4 cirosu / platform harcaması. Google Ads başlığına tıklayınca marka / marka dışı / PMax açılır.
        "Diğer" = diğer arama motorları, e-posta, AI asistanları, organik sosyal, referans ve atanmamış. 10'dan az rezervasyonda yüzde yerine adet farkı yazılır.
        * kısmi dönemde günlük ortalamaya göre. Harcama verisi Ekim 2025'ten başlıyor.
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- dönem özeti (tüm metrikler) */

/** Dönem satırından türetilen metrikler. Reklam = Google Ads + Meta Ads (GA4 oturum kaynağı). */
const ozetMetrik = (x) => {
  const reklamRez = x.k.google_ads.rez + x.k.meta_ads.rez;
  const reklamCiro = x.k.google_ads.ciro + x.k.meta_ads.ciro;
  const harcama = x.googleHarcama + x.metaHarcama;
  return {
    oturum: x.toplam.oturum,
    rez: x.toplam.rez,
    cr: x.toplam.oturum ? x.toplam.rez / x.toplam.oturum : null,
    ciro: x.toplam.ciro,
    aov: x.toplam.rez ? x.toplam.ciro / x.toplam.rez : null,
    reklamRez,
    reklamPay: x.toplam.rez ? reklamRez / x.toplam.rez : null,
    harcama,
    roas: harcama ? reklamCiro / harcama : null,
    cpa: harcama && reklamRez ? harcama / reklamRez : null
  };
};
// toplam: kısmi dönemde günlük ortalamayla kıyaslanır · harcamaya bağlı: harcama verisi yoksa boş
const OZET_KOLON = [
  { key: 'oturum', label: 'Oturum', fmt: (v) => nf(v), toplam: true },
  { key: 'rez', label: 'Rezervasyon', fmt: (v) => nf(v), toplam: true, kalin: true },
  { key: 'cr', label: 'Dönüşüm oranı', fmt: (v) => (v == null ? '—' : pct(v, 2)) },
  { key: 'ciro', label: 'Ciro', fmt: (v) => (v ? tlShort(v) : '—'), toplam: true, kalin: true },
  { key: 'aov', label: 'Ort. rez. değeri', fmt: (v) => (v == null ? '—' : tl(v)) },
  { key: 'reklamRez', label: 'Reklamdan rez.', fmt: (v) => nf(v), toplam: true, grup: true },
  { key: 'reklamPay', label: 'Reklam payı', fmt: (v) => (v == null ? '—' : pct(v)) },
  { key: 'harcama', label: 'Reklam harcaması', fmt: (v) => (v ? tlShort(v) : '—'), toplam: true, notr: true, harcamali: true },
  { key: 'roas', label: 'ROAS', fmt: roasYazi, kalin: true, harcamali: true },
  { key: 'cpa', label: 'Rez. başı maliyet', fmt: (v) => (v == null ? '—' : tl(v)), ters: true, harcamali: true }
];

function DonemOzetTablosu({ data, loading, grain, setGrain }) {
  const donemler = data?.donemler || [];
  const g = data?.grain || grain;
  const oncesi = (i) => (i + 1 < donemler.length ? donemler[i + 1] : data?.onceki);
  const gunOf = (x) => (x ? x.gunSayisi || dayCount(x.bas, x.son) : 0);
  const gapMi = (x) => x.bas <= '2026-01-08' && x.son >= '2025-12-09';
  const harcamaYok = (x) => !x || x.son < (data?.meta?.spendFrom || '');
  const th = { padding: '10px 14px', fontWeight: 500, fontSize: 12, color: C.muted, textAlign: 'right', whiteSpace: 'nowrap', background: '#fff', borderBottom: `1px solid ${C.line}` };

  const satir = (x, i, toplamSatiri) => {
    const o = toplamSatiri ? null : oncesi(i);
    const kismi = !toplamSatiri && x.kismi;
    const ozelGun = !toplamSatiri && (gapMi(x) || (o && gapMi(o)));
    const m = ozetMetrik(x);
    const om = o ? ozetMetrik(o) : null;
    const zemin = toplamSatiri ? '#FAFBFF' : '#fff';
    return (
      <tr key={toplamSatiri ? 'toplam' : x.bas} style={{ background: zemin }}>
        <td style={{ padding: '10px 14px 10px 24px', whiteSpace: 'nowrap', fontWeight: 600, color: C.ink, fontSize: 14, verticalAlign: 'top', position: 'sticky', left: 0, background: zemin, zIndex: 1,
          borderTop: toplamSatiri ? `2px solid ${C.line}` : `1px solid ${C.hair}` }}>
          {toplamSatiri ? 'Seçili aralık' : donemEtiket(x, g)}
          <div style={{ fontSize: 11, fontWeight: 500, color: C.faint, marginTop: 2 }}>
            {toplamSatiri ? aralikEtiket(data.start, data.end) : kismi ? `kısmi · ${gunOf(x)} gün` : gapMi(x) ? 'GA4 ölçüm eksik' : ''}
          </div>
        </td>
        {OZET_KOLON.map((c) => {
          const bos = c.harcamali && harcamaYok(x);
          const oBos = !om || (c.harcamali && harcamaYok(o));
          const d = bos || oBos || ozelGun ? null
            : degisim(m[c.key], om[c.key], gunOf(x), gunOf(o), c.toplam ? 'rez' : 'oran', kismi && c.toplam);
          return (
            <Hucre key={c.key} v={bos ? null : m[c.key]} fmt={bos ? () => '—' : c.fmt} d={d} notr={c.notr} ters={c.ters}
              kalin={c.kalin} sol={c.grup} kismi={kismi && c.toplam} bg={c.grup || c.key === 'harcama' || c.key === 'roas' || c.key === 'cpa' || c.key === 'reklamPay' ? (toplamSatiri ? undefined : '#FBFCFF') : undefined} />
          );
        })}
      </tr>
    );
  };

  return (
    <Card style={{ padding: '18px 0 6px' }}>
      <div style={{ padding: '0 24px', display: 'flex', justifyContent: 'flex-end' }}>
        <SegBtn items={GRAIN} value={grain} onChange={setGrain} />
      </div>
      {!data && loading ? <div style={{ padding: 24, fontSize: 13, color: C.muted }}>Yükleniyor…</div> : null}
      {donemler.length ? (
        <div className="overflow-auto" style={{ marginTop: 14, maxHeight: 820, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 14, minWidth: 1180 }} className="tabular-nums">
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th rowSpan={2} style={{ ...th, textAlign: 'left', paddingLeft: 24, position: 'sticky', left: 0, zIndex: 4, verticalAlign: 'bottom' }}>Dönem</th>
                <th colSpan={5} style={{ ...th, textAlign: 'center', color: C.ink2, fontWeight: 600, borderBottom: 0, paddingBottom: 4 }}>Tüm site</th>
                <th colSpan={5} style={{ ...th, textAlign: 'center', color: C.accent, fontWeight: 700, background: C.accentSoft, borderBottom: 0, borderLeft: `1px solid ${C.line}`, borderTopLeftRadius: 10, borderTopRightRadius: 10, paddingBottom: 4 }}>
                  Reklam · Google Ads + Meta Ads
                </th>
              </tr>
              <tr>
                {OZET_KOLON.map((c, i) => (
                  <th key={c.key} style={{ ...th, color: c.kalin ? C.ink2 : C.muted, fontWeight: c.kalin ? 600 : 500, background: i >= 5 ? C.accentSoft : '#fff',
                    borderLeft: c.grup ? `1px solid ${C.line}` : undefined, paddingRight: i === OZET_KOLON.length - 1 ? 24 : 14 }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {donemler.map((x, i) => satir(x, i, false))}
              {data?.toplam ? satir(data.toplam, -1, true) : null}
            </tbody>
          </table>
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: C.faint, padding: '14px 24px 10px', lineHeight: 1.6 }}>
        Her hücrede bir önceki {g === 'month' ? 'aya' : g === 'week' ? 'haftaya' : 'güne'} göre değişim. Rezervasyon başı maliyette düşüş yeşil.
        Reklamdan rezervasyon = GA4'te oturumu Google Ads ya da Meta'dan gelen rezervasyon; ROAS = bu rezervasyonların cirosu / Google + Meta harcaması.
        * kısmi dönemde günlük ortalamaya göre. Harcama verisi Ekim 2025'ten başlıyor.
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- kampanya tabloları */

const TIP_ETIKET = { marka: 'Marka', marka_disi: 'Marka dışı', pmax: 'PMax', diger: 'Diğer', eslesmeyen: 'Eşleşmeyen' };
const TIP_RENK = { marka: '#436AEB', marka_disi: '#0E7490', pmax: '#7C3AED', diger: '#6B7280', eslesmeyen: '#9CA3AF' };

const KAMPANYA_KOLON = [
  { key: 'harcama', label: 'Harcama', fmt: (v) => (v ? tl(v) : '—') },
  { key: 'tiklama', label: 'Tıklama', fmt: (v) => (v ? nf(v) : '—') },
  { key: 'oturum', label: 'GA4 oturum', fmt: (v) => nf(v) },
  { key: 'rez', label: 'Rezervasyon', fmt: (v) => nf(v) },
  { key: 'cr', label: 'Dönüşüm', fmt: (v) => (v == null ? '—' : pct(v, 2)) },
  { key: 'cpa', label: 'Rez. başı', fmt: (v) => (v == null ? '—' : tl(v)) },
  { key: 'ciro', label: 'Ciro', fmt: (v) => (v ? tlShort(v) : '—') },
  { key: 'roas', label: 'ROAS', fmt: roasYazi }
];
const turet = (k) => ({
  ...k,
  cr: k.oturum ? k.rez / k.oturum : null,
  cpa: k.harcama && k.rez ? k.harcama / k.rez : null,
  roas: k.harcama ? k.ciro / k.harcama : null
});

function KampanyaTablosu({ rows, platform, loading }) {
  const [tip, setTip] = useState('hepsi');
  const [sira, setSira] = useState({ key: 'rez', desc: true });
  const [hepsi, setHepsi] = useState(false);
  const renk = platform === 'google' ? RENK.google_ads : RENK.meta_ads;

  const tipler = useMemo(() => {
    if (platform !== 'google') return [];
    const say = {};
    for (const r of rows) say[r.tip] = (say[r.tip] || 0) + 1;
    return [{ key: 'hepsi', label: 'Tümü' }, ...['marka', 'marka_disi', 'pmax', 'diger', 'eslesmeyen'].filter((t) => say[t]).map((t) => ({ key: t, label: TIP_ETIKET[t] }))];
  }, [rows, platform]);

  const liste = useMemo(() => {
    const l = rows.filter((r) => tip === 'hepsi' || r.tip === tip).map(turet);
    const { key, desc } = sira;
    l.sort((a, b) => {
      const av = a[key] ?? -Infinity; const bv = b[key] ?? -Infinity;
      return (desc ? bv - av : av - bv) || b.harcama - a.harcama;
    });
    return l;
  }, [rows, tip, sira]);

  const top = liste.reduce((a, x) => ({ harcama: a.harcama + x.harcama, tiklama: a.tiklama + x.tiklama, oturum: a.oturum + x.oturum, rez: a.rez + x.rez, ciro: a.ciro + x.ciro }),
    { harcama: 0, tiklama: 0, oturum: 0, rez: 0, ciro: 0 });
  const topT = turet(top);
  const maxRez = Math.max(1, ...liste.map((x) => x.rez));
  const gorunen = hepsi ? liste : liste.slice(0, 15);
  const th = { padding: '10px 10px', fontWeight: 500, fontSize: 12, color: C.muted, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.line}`, cursor: 'pointer', userSelect: 'none' };
  const sirala = (key) => setSira((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: true }));

  return (
    <Card style={{ padding: '18px 0 6px' }}>
      {tipler.length > 2 ? (
        <div style={{ padding: '0 24px 4px' }}><SegBtn items={tipler} value={tip} onChange={(k) => { setTip(k); setHepsi(false); }} /></div>
      ) : null}
      {!rows.length && loading ? <div style={{ padding: 24, fontSize: 13, color: C.muted }}>Yükleniyor…</div> : null}
      {!rows.length && !loading ? <div style={{ padding: 24, fontSize: 14, color: C.muted }}>Bu aralıkta kampanya yok.</div> : null}
      {liste.length ? (
        <div className="overflow-x-auto" style={{ marginTop: 10, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 14, minWidth: 1000 }} className="tabular-nums">
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', paddingLeft: 24, cursor: 'default' }}>Kampanya</th>
                {KAMPANYA_KOLON.map((c) => (
                  <th key={c.key} style={{ ...th, color: sira.key === c.key ? C.ink : C.muted, fontWeight: sira.key === c.key ? 600 : 500, paddingRight: c.key === 'roas' ? 24 : 10 }} onClick={() => sirala(c.key)}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      {c.label}
                      {sira.key === c.key ? (sira.desc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : null}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gorunen.map((k) => (
                <tr key={`${k.ad}-${k.tip || ''}`} style={{ borderTop: `1px solid ${C.hair}` }}>
                  <td style={{ padding: '12px 12px 12px 24px', borderTop: `1px solid ${C.hair}` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: 320 }}>
                      <span title={k.durum === 'ENABLED' || k.durum === 'ACTIVE' ? 'Açık' : k.durum ? 'Kapalı / duraklatılmış' : 'Platform verisi yok'}
                        style={{ width: 7, height: 7, borderRadius: 4, flexShrink: 0, marginTop: 6, background: k.durum === 'ENABLED' || k.durum === 'ACTIVE' ? '#10B981' : '#D1D5DB' }} />
                      <span style={{ fontWeight: 600, color: C.ink, lineHeight: 1.35, wordBreak: 'break-word' }}>{k.ad}</span>
                    </div>
                    {platform === 'google' && k.tip ? (
                      <div style={{ marginTop: 3, marginLeft: 15, fontSize: 11.5, fontWeight: 600, color: TIP_RENK[k.tip] || C.muted }}>{TIP_ETIKET[k.tip]}</div>
                    ) : null}
                  </td>
                  {KAMPANYA_KOLON.map((c) => (
                    <td key={c.key} style={{ padding: '12px 10px', paddingRight: c.key === 'roas' ? 24 : 10, textAlign: 'right', whiteSpace: 'nowrap', borderTop: `1px solid ${C.hair}`,
                      color: c.key === 'rez' || c.key === 'roas' ? C.ink : C.ink2, fontWeight: c.key === 'rez' || c.key === 'roas' ? 600 : 400 }}>
                      {c.key === 'rez' ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                          <div style={{ width: 56, height: 6, background: C.hair, borderRadius: 3 }}>
                            <div style={{ width: `${(k.rez / maxRez) * 100}%`, height: 6, borderRadius: 3, background: renk, marginLeft: 'auto' }} />
                          </div>
                          <span style={{ minWidth: 36 }}>{nf(k.rez)}</span>
                        </div>
                      ) : c.fmt(k[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
              <tr style={{ background: '#FAFBFF' }}>
                <td style={{ padding: '12px 14px 12px 24px', fontWeight: 700, borderTop: `2px solid ${C.line}` }}>
                  Toplam <span style={{ color: C.faint, fontWeight: 500, fontSize: 12.5 }}>· {liste.length} kampanya</span>
                </td>
                {KAMPANYA_KOLON.map((c) => (
                  <td key={c.key} style={{ padding: '12px 10px', paddingRight: c.key === 'roas' ? 24 : 10, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', borderTop: `2px solid ${C.line}` }}>
                    {c.fmt(topT[c.key])}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
      {liste.length > 15 ? (
        <div className="pdf-gizle" style={{ padding: '10px 24px 4px' }}>
          <button type="button" onClick={() => setHepsi((v) => !v)}
            style={{ border: 0, background: 'transparent', color: C.accent, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', padding: 0 }}>
            {hepsi ? 'Daha az göster' : `Tüm ${liste.length} kampanyayı göster`}
          </button>
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: C.faint, padding: '12px 24px 10px', lineHeight: 1.6 }}>
        {platform === 'google'
          ? 'Harcama ve tıklama Google Ads hesabından; oturum, rezervasyon ve ciro GA4\'te oturumun geldiği kampanyadan (auto-tagging ile kampanya adı eşleşir). Harcaması olmayan satırlar aralıkta harcama yapmamış ama GA4\'te rezervasyon getirmiş eski ya da adı eşleşmeyen kampanyalardır.'
          : 'Harcama ve tıklama Meta hesabından; oturum, rezervasyon ve ciro GA4\'te UTM kampanya adından (utm_campaign Meta kampanya adıyla eşleştirilir). Meta\'nın kendi panelinde saydığı dönüşümler burada yok; GA4 son tıklamada Meta\'ya genellikle az rezervasyon yazar.'}
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- lokasyon */

function LokasyonTablosu({ data, loading, onBuYil }) {
  const [ara, setAra] = useState('');
  const [hepsi, setHepsi] = useState(false);

  // Sütunlar eskiden yeniye; ölçülmeyen aylar (dolu = 0) sağda soluk kalır.
  const aylar = useMemo(() => (data?.aylar || []).slice().reverse(), [data]);
  const olculen = aylar.filter((a) => data?.ayToplam[a]?.dolu > 0);
  const liste = useMemo(() => {
    const q = ara.trim().toLocaleLowerCase('tr-TR');
    return (data?.ofisler || []).filter((o) => !q || o.ad.toLocaleLowerCase('tr-TR').includes(q));
  }, [data, ara]);
  const gorunen = hepsi || ara ? liste : liste.slice(0, 25);
  const maxHucre = Math.max(1, ...(data?.ofisler || []).slice(0, 40).flatMap((o) => Object.values(o.aylar)));
  const toplamDolu = olculen.reduce((a, m) => a + data?.ayToplam[m].dolu, 0);

  if (data && !olculen.length && !loading) {
    return (
      <Card style={{ padding: '28px 28px' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: C.ink }}>Bu aralıkta lokasyon verisi yok</div>
        <div style={{ fontSize: 14, color: C.muted, marginTop: 6, lineHeight: 1.55, maxWidth: 720 }}>
          GA4 alış ofisini yalnız Ocak–Ağustos 2026 rezervasyonlarında kaydediyor. 1 Eylül 2026'dan beri site bu alanı rezervasyonla birlikte göndermiyor.
        </div>
        <button type="button" onClick={onBuYil}
          style={{ marginTop: 16, border: 0, background: C.accent, color: '#fff', borderRadius: 10, padding: '9px 16px', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>
          Bu yılı göster
        </button>
      </Card>
    );
  }

  const hucreBg = (n) => (n ? `rgba(67,106,235,${0.05 + Math.min(n / maxHucre, 1) * 0.32})` : 'transparent');
  const th = { padding: '10px 12px', fontWeight: 500, fontSize: 12, color: C.muted, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.line}`, background: '#fff' };

  return (
    <Card style={{ padding: '18px 0 6px' }}>
      <div style={{ padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div className="pdf-gizle" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F3F4F8', borderRadius: 10, padding: '7px 12px', width: 300, maxWidth: '100%' }}>
          <Search size={15} style={{ color: C.faint }} />
          <input value={ara} onChange={(e) => setAra(e.target.value)} placeholder="Ofis ara (ör. Sabiha, İzmir)"
            style={{ border: 0, background: 'transparent', outline: 'none', fontSize: 13.5, width: '100%', color: C.ink, fontFamily: FONT }} />
        </div>
        {data ? (
          <div style={{ fontSize: 13, color: C.muted }} className="tabular-nums">
            {nf(data.ofisler.length)} ofis · lokasyonu bilinen {nf(toplamDolu)} rezervasyon
          </div>
        ) : null}
      </div>
      {!data && loading ? <div style={{ padding: 24, fontSize: 13, color: C.muted }}>Yükleniyor…</div> : null}
      {data ? (
        <div className="overflow-auto" style={{ marginTop: 14, maxHeight: 760, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 300 + aylar.length * 78 }} className="tabular-nums">
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ ...th, textAlign: 'left', paddingLeft: 24, position: 'sticky', left: 0, zIndex: 4 }}>Alış ofisi</th>
                {aylar.map((a) => {
                  const t = data.ayToplam[a];
                  const yok = !t.dolu; const kismi = t.dolu && t.dolu < t.rez * 0.9;
                  return (
                    <th key={a} style={{ ...th, color: yok ? '#C4C9D4' : C.muted }}>
                      {ayKisa(a)}
                      {yok || kismi ? <div style={{ fontSize: 10.5, fontWeight: 500, color: yok ? '#C4C9D4' : '#B45309' }}>{yok ? 'ölçülmüyor' : 'kısmi'}</div> : null}
                    </th>
                  );
                })}
                <th style={{ ...th, color: C.ink, fontWeight: 600, borderLeft: `1px solid ${C.line}` }}>Toplam</th>
                <th style={{ ...th, paddingRight: 24 }}>Ort. değer</th>
              </tr>
            </thead>
            <tbody>
              {gorunen.map((o) => (
                <tr key={o.ad}>
                  <td style={{ padding: '9px 12px 9px 24px', fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', borderTop: `1px solid ${C.hair}`, maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.ad}>
                    {o.ad}
                  </td>
                  {aylar.map((a) => {
                    const n = o.aylar[a] || 0; const yok = !data.ayToplam[a].dolu;
                    return (
                      <td key={a} style={{ padding: '9px 12px', textAlign: 'right', borderTop: `1px solid ${C.hair}`, background: yok ? '#FAFAFB' : hucreBg(n), color: n ? C.ink : '#D1D5DB', fontWeight: n ? 500 : 400 }}>
                        {yok ? '' : n ? nf(n) : '·'}
                      </td>
                    );
                  })}
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, borderTop: `1px solid ${C.hair}`, borderLeft: `1px solid ${C.line}` }}>{nf(o.rez)}</td>
                  <td style={{ padding: '9px 24px 9px 12px', textAlign: 'right', color: C.ink2, borderTop: `1px solid ${C.hair}` }}>{o.rez ? tl(o.ciro / o.rez) : '—'}</td>
                </tr>
              ))}
              <tr style={{ background: '#FAFBFF' }}>
                <td style={{ padding: '10px 12px 10px 24px', fontWeight: 700, position: 'sticky', left: 0, background: '#FAFBFF', borderTop: `2px solid ${C.line}` }}>Lokasyonu bilinen</td>
                {aylar.map((a) => (
                  <td key={a} style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, borderTop: `2px solid ${C.line}`, color: data.ayToplam[a].dolu ? C.ink : '#C4C9D4' }}>
                    {data.ayToplam[a].dolu ? nf(data.ayToplam[a].dolu) : '—'}
                  </td>
                ))}
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, borderTop: `2px solid ${C.line}`, borderLeft: `1px solid ${C.line}` }}>{nf(toplamDolu)}</td>
                <td style={{ borderTop: `2px solid ${C.line}` }} />
              </tr>
              <tr style={{ background: '#FAFBFF' }}>
                <td style={{ padding: '6px 12px 10px 24px', color: C.muted, fontSize: 12.5, position: 'sticky', left: 0, background: '#FAFBFF' }}>Tüm rezervasyonlar (GA4)</td>
                {aylar.map((a) => (
                  <td key={a} style={{ padding: '6px 12px 10px', textAlign: 'right', color: C.muted, fontSize: 12.5 }}>{nf(data.ayToplam[a].rez)}</td>
                ))}
                <td style={{ padding: '6px 12px 10px', textAlign: 'right', color: C.muted, fontSize: 12.5, borderLeft: `1px solid ${C.line}` }}>
                  {nf(aylar.reduce((s, a) => s + data.ayToplam[a].rez, 0))}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
      {liste.length > 25 && !ara ? (
        <div className="pdf-gizle" style={{ padding: '10px 24px 4px' }}>
          <button type="button" onClick={() => setHepsi((v) => !v)}
            style={{ border: 0, background: 'transparent', color: C.accent, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', padding: 0 }}>
            {hepsi ? 'İlk 25 ofisi göster' : `Tüm ${liste.length} ofisi göster`}
          </button>
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: C.faint, padding: '12px 24px 10px', lineHeight: 1.6 }}>
        Rezervasyonun alış ofisi, GA4 purchase olayındaki pickup_city alanından. Alan Ocak 2026'da tanımlandı ve 1 Eylül 2026'dan beri site tarafından gönderilmiyor;
        bu yüzden 2025 ve Eylül 2026 sonrası "ölçülmüyor" görünür. Renk yoğunluğu hücredeki rezervasyon adedine göre.
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- tarih seçici */

/** Başlığın sağ üstündeki tarih seçici — tek satır tetikleyici, tıklayınca açılan menü. */
function TarihSecici({ araKey, secAralik, ozel, setOzel, uygulaOzel, range, loading, onYenile }) {
  const [acik, setAcik] = useState(false);
  const kutu = useRef(null);
  const dun = aralikOf('30')[1];

  useEffect(() => {
    if (!acik) return undefined;
    const disari = (e) => { if (kutu.current && !kutu.current.contains(e.target)) setAcik(false); };
    const esc = (e) => { if (e.key === 'Escape') setAcik(false); };
    document.addEventListener('mousedown', disari);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', disari); document.removeEventListener('keydown', esc); };
  }, [acik]);

  const etiket = ARALIKLAR.find((a) => a.key === araKey)?.label || 'Tarih aralığı';
  const kisaAralik = (r) => (r ? aralikEtiket(r[0], r[1]) : '');
  const tarihInput = { width: '100%', minWidth: 0, boxSizing: 'border-box', border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 6px', background: '#fff', fontSize: 13, color: C.ink, fontFamily: FONT };

  return (
    <div ref={kutu} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => setAcik((v) => !v)} aria-expanded={acik}
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer', fontFamily: FONT,
            background: '#fff', border: `1px solid ${acik ? C.accent : C.line}`, borderRadius: 12, padding: '9px 12px',
            boxShadow: acik ? `0 0 0 3px ${C.accentSoft}` : '0 1px 2px rgba(17,24,39,.05)', transition: 'border-color .15s, box-shadow .15s' }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: C.accentSoft, color: C.accent, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Calendar size={15} />
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: C.ink, lineHeight: 1.2 }}>{etiket}</span>
            <span style={{ display: 'block', fontSize: 12, color: C.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="tabular-nums">
              {kisaAralik(range)}
            </span>
          </span>
          <ChevronDown size={16} style={{ color: C.faint, flexShrink: 0, transform: acik ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        <button type="button" onClick={onYenile} title="Veriyi yenile"
          style={{ border: `1px solid ${C.line}`, background: '#fff', borderRadius: 12, width: 50, display: 'grid', placeItems: 'center', cursor: 'pointer', color: C.ink2, flexShrink: 0 }}>
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {acik ? (
        <div role="listbox"
          style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, zIndex: 30, background: 'rgba(255,255,255,.98)', backdropFilter: 'blur(12px)',
            border: `1px solid ${C.line}`, borderRadius: 14, boxShadow: '0 12px 40px rgba(17,24,39,.14), 0 2px 6px rgba(17,24,39,.06)', padding: 6 }}>
          {ARALIKLAR.filter((a) => a.key !== 'ozel').map((a) => {
            const on = araKey === a.key;
            return (
              <button key={a.key} type="button" role="option" aria-selected={on}
                onClick={() => { secAralik(a.key); setAcik(false); }}
                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = '#F3F4F8'; }}
                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
                style={{ width: '100%', border: 0, cursor: 'pointer', borderRadius: 9, padding: '8px 10px', fontFamily: FONT, textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8, background: on ? C.accentSoft : 'transparent' }}>
                <span style={{ width: 14, color: C.accent, display: 'grid', placeItems: 'center' }}>{on ? <Check size={14} strokeWidth={2.5} /> : null}</span>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? C.accent : C.ink, whiteSpace: 'nowrap' }}>{a.label}</span>
                <span style={{ fontSize: 11.5, color: C.faint, whiteSpace: 'nowrap' }} className="tabular-nums">{kisaAralik(aralikOf(a.key))}</span>
              </button>
            );
          })}
          <div style={{ height: 1, background: C.hair, margin: '6px 4px' }} />
          <div style={{ padding: '6px 10px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: araKey === 'ozel' ? 600 : 500, color: araKey === 'ozel' ? C.accent : C.ink }}>
              <span style={{ width: 14, display: 'grid', placeItems: 'center' }}>{araKey === 'ozel' ? <Check size={14} strokeWidth={2.5} /> : null}</span>
              Özel aralık
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 6, marginTop: 8 }}>
              <input type="date" aria-label="Başlangıç" value={ozel[0]} min="2024-01-01" max={ozel[1]} onChange={(e) => setOzel([e.target.value, ozel[1]])} style={tarihInput} />
              <input type="date" aria-label="Bitiş" value={ozel[1]} min={ozel[0]} max={dun} onChange={(e) => setOzel([ozel[0], e.target.value])} style={tarihInput} />
            </div>
            <button type="button" onClick={() => { secAralik('ozel'); uygulaOzel(); setAcik(false); }}
              style={{ width: '100%', marginTop: 8, border: 0, background: C.accent, color: '#fff', borderRadius: 9, padding: '8px 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Uygula
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- PDF */

/**
 * PDF: sayfanın o anki görünümü gizli bir iframe'e kopyalanır ve orada yazdırılır (tarayıcının
 * "PDF olarak kaydet"i). Panelin yan menüsü/kaydırma kutuları baskıyı tek sayfaya kırpacağı için
 * sayfanın kendisi yazdırılmaz. Metin vektör kalır (html2canvas gibi resim değil), tablo başlıkları
 * her sayfada tekrarlanır. Açık "Tümünü göster" listeleri olduğu gibi basılır.
 *
 * Baskıda medya sorguları kağıt genişliğine göre çalışır (xl/2xl tutmaz) → ızgaralar .pdf-* ile sabitlenir.
 * zoom 0.66: ~1560px düzen yatay A4'ün ~1030px yazdırılabilir alanına sığar.
 */
const PDF_CSS = `
@page { size: A4 landscape; margin: 9mm 8mm; }
html, body { margin: 0; background: ${C.ground}; }
body { zoom: 0.66; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pdf-kok { width: 1560px; padding: 8px 4px; font-family: ${FONT}; color: ${C.ink}; }
.pdf-gizle { display: none !important; }
.pdf-kpi { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; }
.pdf-iki { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
.overflow-auto, .overflow-x-auto { max-height: none !important; overflow: visible !important; opacity: 1 !important; }
thead { display: table-header-group; }
th, td, thead { position: static !important; }
tr { break-inside: avoid; page-break-inside: avoid; }
h1, h2 { break-after: avoid; page-break-after: avoid; }
#ozet { break-after: avoid; }
* { box-shadow: none !important; transition: none !important; animation: none !important; }
`;

function pdfIndir(kaynak, baslik) {
  if (!kaynak) return;
  const eski = document.getElementById('kaynak-pdf-iframe');
  if (eski) eski.remove();
  const iframe = document.createElement('iframe');
  iframe.id = 'kaynak-pdf-iframe';
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '1600px', height: '900px', border: '0', opacity: '0', pointerEvents: 'none', zIndex: '-1' });
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const stiller = [...document.querySelectorAll('style, link[rel="stylesheet"]')].map((n) => n.outerHTML).join('\n');
  const kok = kaynak.cloneNode(true);
  kok.removeAttribute('style');
  kok.className = 'pdf-kok';
  const olusturma = new Date().toLocaleString('tr-TR', { dateStyle: 'long', timeStyle: 'short' });
  doc.open();
  doc.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${baslik.replace(/</g, '')}</title>${stiller}<style>${PDF_CSS}</style></head>
    <body>${kok.outerHTML}<div style="font: 12px ${FONT}; color: ${C.faint}; padding: 12px 4px 0;">Glowytics · ${olusturma} tarihinde oluşturuldu</div></body></html>`);
  doc.close();

  let basildi = false;
  const yazdir = () => {
    if (basildi) return;
    basildi = true;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      // print() diyalog kapanana kadar bekler; sonra iframe temizlenir.
      setTimeout(() => iframe.remove(), 1000);
    }
  };
  // Harici stil dosyaları yüklensin diye kısa bekleme (CRA dev server stilleri satır içi <style> verir).
  const linkler = [...doc.querySelectorAll('link[rel="stylesheet"]')];
  if (!linkler.length) { setTimeout(yazdir, 150); return; }
  let kalan = linkler.length;
  const bitti = () => { kalan -= 1; if (kalan <= 0) setTimeout(yazdir, 150); };
  linkler.forEach((l) => { l.addEventListener('load', bitti); l.addEventListener('error', bitti); });
  setTimeout(yazdir, 3000);
}

/* ---------------------------------------------------------------- sayfa */

export default function EuropcarKaynakRaporu() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [araKey, setAraKey] = useState('buyil');
  const [range, setRange] = useState(() => aralikOf('buyil'));
  const [ozel, setOzel] = useState(() => aralikOf('buyil'));
  const [grain, setGrain] = useState(() => varsayilanGrain(aralikOf('buyil')));
  const [refreshTick, setRefreshTick] = useState(0);

  const [detay, setDetay] = useState(null);
  const [tablo, setTablo] = useState(null);
  const [lok, setLok] = useState(null);
  const [yukleniyor, setYukleniyor] = useState({ detay: false, tablo: false, lok: false });

  const secAralik = (k) => {
    setAraKey(k);
    if (k === 'ozel') return;
    const r = aralikOf(k);
    setRange(r); setOzel(r); setGrain(varsayilanGrain(r));
  };
  const uygulaOzel = () => { setRange(ozel); setGrain(varsayilanGrain(ozel)); };

  const getir = useCallback(async (yol, alan, set, refresh) => {
    setYukleniyor((y) => ({ ...y, [alan]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/europcar-kaynak/${yol}${refresh ? '&refresh=1' : ''}`, { credentials: 'include', headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Rapor yüklenemedi');
      set(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setYukleniyor((y) => ({ ...y, [alan]: false }));
    }
  }, [headers]);

  useEffect(() => {
    if (!brandId) return;
    const q = `start=${range[0]}&end=${range[1]}`;
    getir(`detail?${q}`, 'detay', setDetay, refreshTick > 0);
    getir(`locations?${q}`, 'lok', setLok, refreshTick > 0);
  }, [brandId, range, getir, refreshTick]);
  useEffect(() => {
    if (!brandId) return;
    getir(`table?start=${range[0]}&end=${range[1]}&grain=${grain}`, 'tablo', (j) => setTablo({ ...j, istenenStart: range[0] }), refreshTick > 0);
  }, [brandId, range, grain, getir, refreshTick]);

  const loading = yukleniyor.detay || yukleniyor.tablo || yukleniyor.lok;
  const raporRef = useRef(null);
  const by = useMemo(() => Object.fromEntries((detay?.kaynaklar || []).map((k) => [k.key, k])), [detay]);
  const t = detay?.toplam || { rez: 0, ciro: 0, oturum: 0 };
  const gH = detay?.harcama?.google || 0;
  const mH = detay?.harcama?.meta || 0;
  const gK = by.google_ads || { rez: 0, ciro: 0 };
  const mK = by.meta_ads || { rez: 0, ciro: 0 };

  const rezPay = useMemo(() => {
    const ana = ['google_ads', 'meta_ads', 'google_organik', 'direkt'];
    const etiket = { google_ads: 'Google Ads', meta_ads: 'Meta Ads', google_organik: 'Google Organik', direkt: 'Direkt' };
    const diger = (detay?.kaynaklar || []).filter((k) => !ana.includes(k.key)).reduce((a, k) => a + k.rez, 0);
    return [...ana.map((k) => ({ key: k, label: etiket[k], v: by[k]?.rez || 0 })), { key: 'diger', label: 'Diğer', v: diger }];
  }, [detay, by]);

  return (
    <div style={{ background: C.ground, minHeight: '100%', fontFamily: FONT, color: C.ink }} className="px-4 sm:px-8 py-8">
      <div ref={raporRef} style={{ maxWidth: 1640, margin: '0 auto' }}>
        <main style={{ minWidth: 0 }}>
          {/* Başlık — tarih seçici sağ üstte */}
          <div id="ozet" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.accent }}>Europcar Türkiye</div>
              <h1 style={{ fontSize: 44, fontWeight: 700, letterSpacing: '-0.03em', margin: '4px 0 0', lineHeight: 1.05 }}>Kaynak Raporu</h1>
              <div style={{ fontSize: 17, color: C.muted, marginTop: 10, lineHeight: 1.45 }}>
                {aralikEtiket(range[0], range[1])} arasında rezervasyonlar nereden geldi, reklama ne harcandı.
              </div>
            </div>
            <div className="pdf-gizle" style={{ display: 'flex', gap: 8, width: 420, maxWidth: '100%', position: 'relative', zIndex: 20 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TarihSecici araKey={araKey} secAralik={secAralik} ozel={ozel} setOzel={setOzel} uygulaOzel={uygulaOzel}
                  range={range} loading={loading} onYenile={() => setRefreshTick((x) => x + 1)} />
              </div>
              <button type="button" onClick={() => pdfIndir(raporRef.current, `Europcar Kaynak Raporu ${aralikEtiket(range[0], range[1])}`)}
                disabled={loading} title={loading ? 'Veri yükleniyor…' : 'Sayfayı PDF olarak kaydet'}
                style={{ display: 'flex', alignItems: 'center', gap: 7, border: 0, borderRadius: 12, padding: '0 16px', background: C.ink, color: '#fff',
                  fontSize: 13.5, fontWeight: 600, fontFamily: FONT, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.5 : 1, flexShrink: 0 }}>
                <FileDown size={16} /> PDF
              </button>
            </div>
          </div>

          {/* Özet kartları — yalnız seçili aralık */}
          <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-5 pdf-kpi" style={{ gap: 14, marginTop: 28, opacity: yukleniyor.detay ? 0.55 : 1, transition: 'opacity .15s' }}>
            <Kpi label="Rezervasyon" value={nf(t.rez)}
              lines={[['Reklamdan', t.rez ? pct((gK.rez + mK.rez) / t.rez) : '—'], ['Dönüşüm oranı', t.oturum ? pct(t.rez / t.oturum, 2) : '—']]} />
            <Kpi label="Ciro" value={tlShort(t.ciro)}
              lines={[['Ort. rezervasyon değeri', t.rez ? tl(t.ciro / t.rez) : '—'], ['Kaynak', 'GA4 brüt']]} />
            <Kpi label="Reklam harcaması" value={tlShort(gH + mH)}
              lines={[['Toplam ROAS', gH + mH ? roasYazi((gK.ciro + mK.ciro) / (gH + mH)) : '—'], ['Rez. başı maliyet', gK.rez + mK.rez ? tl((gH + mH) / (gK.rez + mK.rez)) : '—']]} />
            <Kpi label="Google Ads" value={tlShort(gH)} renk={RENK.google_ads}
              lines={[['Rezervasyon', nf(gK.rez)], ['ROAS', gH ? roasYazi(gK.ciro / gH) : '—']]} />
            <Kpi label="Meta Ads" value={tlShort(mH)} renk={RENK.meta_ads}
              lines={[['Rezervasyon', nf(mK.rez)], ['ROAS', mH ? roasYazi(mK.ciro / mH) : '—']]} />
          </div>

          {/* Paylar */}
          {t.rez ? (
            <Card style={{ marginTop: 14, padding: '22px 24px' }}>
              <div className="grid grid-cols-1 lg:grid-cols-2 pdf-iki" style={{ gap: 32 }}>
                <PayCubugu title="Rezervasyon nereden geldi" items={rezPay} toplam={t.rez} fmt={nf} />
                {gH + mH ? (
                  <PayCubugu title="Reklam harcaması nereye gitti" toplam={gH + mH} fmt={tlShort}
                    items={[{ key: 'google_ads', label: 'Google Ads', v: gH }, { key: 'meta_ads', label: 'Meta Ads', v: mH }]} />
                ) : <div style={{ color: C.faint, fontSize: 13 }}>Bu aralıkta harcama yok.</div>}
              </div>
            </Card>
          ) : null}

          {/* Dönem özeti — tüm metrikler */}
          <BolumBaslik id="donemler" eyebrow="Genel bakış"
            title={`${(tablo?.grain || grain) === 'month' ? 'Aylık' : (tablo?.grain || grain) === 'week' ? 'Haftalık' : 'Günlük'} performans`}
            sub="Her dönemin bütün metrikleri tek satırda: trafik, rezervasyon, ciro ve reklamın getirisi." />
          <DonemOzetTablosu data={tablo} loading={yukleniyor.tablo} grain={grain} setGrain={setGrain} />

          {/* Kanal tablosu */}
          <BolumBaslik id="kanallar" eyebrow="Kanallar"
            title={`${(tablo?.grain || grain) === 'month' ? 'Aylık' : (tablo?.grain || grain) === 'week' ? 'Haftalık' : 'Günlük'} kanal tablosu`}
            sub="Her dönemde kaynak başına rezervasyon; Google ve Meta harcaması kendi ROAS'ıyla yan yana." />
          <KanalTablosu data={tablo} loading={yukleniyor.tablo} grain={grain} setGrain={setGrain} />

          {/* Google kampanyaları */}
          <BolumBaslik id="google" eyebrow="Google Ads"
            title="Hangi kampanya kaç rezervasyon getirdi"
            sub={`${aralikEtiket(range[0], range[1])} · ${nf(gK.rez)} rezervasyon, ${tlShort(gH)} harcama. Başlıklara tıklayarak sıralayın.`} />
          <KampanyaTablosu rows={detay?.googleKampanyalar || []} platform="google" loading={yukleniyor.detay} />

          {/* Meta kampanyaları */}
          <BolumBaslik id="meta" eyebrow="Meta Ads"
            title="Meta kampanyaları"
            sub={`${aralikEtiket(range[0], range[1])} · ${nf(mK.rez)} rezervasyon, ${tlShort(mH)} harcama.`} />
          <KampanyaTablosu rows={detay?.metaKampanyalar || []} platform="meta" loading={yukleniyor.detay} />

          {/* Lokasyon */}
          <BolumBaslik id="lokasyon" eyebrow="Lokasyonlar"
            title="Hangi ofis ne kadar rezervasyon alıyor"
            sub="Alış ofisine göre aylık rezervasyon, GA4'ten." />
          <LokasyonTablosu data={lok} loading={yukleniyor.lok} onBuYil={() => secAralik('buyil')} />

          <div style={{ fontSize: 12, color: C.faint, lineHeight: 1.6, marginTop: 28, paddingBottom: 24 }}>
            Rezervasyon ve ciro canlı GA4'ten (oturumun kaynağı, brüt; iptal düşülmez). Harcama Google Ads ve Meta hesaplarından.
            {detay?.meta?.cached ? ' · önbellekten' : ''}
          </div>
        </main>
      </div>
    </div>
  );
}
